import { createRoot } from 'react-dom/client'
import { IconsProvider } from '@talkdesk/cobalt-react-icon'
import ViewportProvider from '@talkdesk/cobalt-react-viewport-provider'
import ThemeProvider from '@talkdesk/cobalt-react-theme-provider'
import PortalProvider from '@talkdesk/cobalt-react-portal-provider'
import RouterRoot from './root'
import theme from '@talkdesk/cobalt-theme-light'
import { ErrorBoundary } from 'react-error-boundary'
import { setupI18N } from './i18n'
import { initTDFeatures } from './config/feature-flags'

window.TalkdeskApps = window.TalkdeskApps || {}

const appRoots = new WeakMap()
// Tracks the latest start() generation per rootElement, so a stop() (or a
// newer start()) that happens before startApp()'s promise settles can make
// the in-flight root a no-op once it resolves, instead of leaking a mounted
// root stop() never saw.
const pendingStarts = new WeakMap()

window.TalkdeskApps.TalkdeskIdLoginUi = {
  start: function (rootElement, params) {
    const existing = appRoots.get(rootElement)
    if (existing) {
      existing.unmount()
      appRoots.delete(rootElement)
    }
    const token = Symbol()
    pendingStarts.set(rootElement, token)
    startApp(rootElement, params).then((root) => {
      if (pendingStarts.get(rootElement) !== token) {
        root.unmount()
        return
      }
      appRoots.set(rootElement, root)
    })
  },
  stop: function (rootElement) {
    pendingStarts.delete(rootElement)
    const existing = appRoots.get(rootElement)
    if (existing) {
      existing.unmount()
      appRoots.delete(rootElement)
    }
  },
  version: __APP_VERSION__,
}

async function startApp(rootElement, params) {
  const { themeVersion, language, language_path } = params

  setupI18N(language, language_path)

  // Don't block first paint on Split IO: the client falls back to
  // configured defaults until ready, so render immediately and let
  // flags apply live once/if the SDK becomes ready.
  initTDFeatures().catch((e) =>
    console.warn(
      'Feature flags failed to initialize, falling back to defaults:',
      e.message,
    ),
  )

  let themeLoader
  if (themeVersion) {
    console.log(`Using theme version: ${themeVersion}`)
    themeLoader = async () => Promise.resolve(null)
  } else {
    console.log('Using default theme')
    themeLoader = async () => Promise.resolve(theme)
  }

  const root = createRoot(rootElement)
  root.render(
    <ErrorBoundary
      fallback={<div />}
      onError={(error, errorInfo) =>
        console.log(`Error: ${error}, Error Info: ${errorInfo}`)
      }
    >
      <ViewportProvider>
        <ThemeProvider loader={themeLoader}>
          <IconsProvider>
            <PortalProvider>
              <RouterRoot {...params} />
            </PortalProvider>
          </IconsProvider>
        </ThemeProvider>
      </ViewportProvider>
    </ErrorBoundary>,
  )
  return root
}
